<template>
  <Teleport :to="store.overlayTarget">
    <Transition name="fade">
      <div v-if="day" class="fixed inset-0 z-[55]" @click="emit('close')"></div>
    </Transition>

    <Transition name="pop">
      <div
        v-if="day"
        class="fixed z-[56] w-64 max-h-[60vh] flex flex-col bg-white dark:bg-[#1C2128] rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden"
        :style="positionStyle"
        role="dialog"
        aria-modal="true"
        :aria-label="`Events on ${headingDate}`"
        @click.stop
      >
        <div class="flex items-start justify-between px-4 pt-3 pb-2 flex-shrink-0">
          <div>
            <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
              {{ weekdayLabel }}
            </p>
            <p class="text-2xl font-normal text-gray-900 dark:text-white leading-tight">{{ dayNumber }}</p>
          </div>
          <button
            @click="emit('close')"
            class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="px-2 pb-2 overflow-y-auto space-y-0.5">
          <button
            v-for="booking in bookings"
            :key="booking.id"
            type="button"
            class="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            @click="onSelect(booking, $event)"
          >
            <span
              class="w-2 h-2 rounded-full flex-shrink-0"
              :style="{ backgroundColor: booking.color_code || booking.backgroundColor || '#3b82f6' }"
            ></span>
            <span class="min-w-0 flex-1">
              <span class="block text-[13px] text-gray-700 dark:text-gray-200 truncate">
                {{ booking.title || booking.name || '(No title)' }}
              </span>
              <span v-if="!booking.all_day" class="block text-[11px] text-gray-400 dark:text-gray-500">
                {{ formatTime(booking.start_at ?? booking.start) }}
              </span>
            </span>
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useCalendarStore } from 'stores/calendar.js'
import { useCalendarGeometry } from 'composables/useCalendarGeometry.js'

// Google's "+N more" opens the day in place rather than navigating away, so the
// month stays on screen. Previously this drilled into day view, which lost the
// month context for what is usually a quick look.

const props = defineProps({
  // { date, bookings, anchor } while open, null when closed
  day: { type: Object, default: null },
})

const emit = defineEmits(['close', 'select'])

const store = useCalendarStore()
const { formatTime } = useCalendarGeometry()

const bookings = computed(() => props.day?.bookings ?? [])

const weekdayLabel = computed(() =>
  props.day ? props.day.date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase() : ''
)
const dayNumber   = computed(() => (props.day ? props.day.date.getDate() : ''))
const headingDate = computed(() =>
  props.day ? props.day.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : ''
)

// Anchored to the day cell, then nudged back inside the viewport so a cell in
// the last column or bottom row does not push the panel off screen.
const positionStyle = computed(() => {
  const anchor = props.day?.anchor
  if (!anchor) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }

  const width  = 256
  const height = Math.min(window.innerHeight * 0.6, 120 + bookings.value.length * 36)

  const left = Math.min(Math.max(8, anchor.left - 8), window.innerWidth - width - 8)
  const top  = Math.min(Math.max(8, anchor.top - 8), window.innerHeight - height - 8)

  return { top: `${top}px`, left: `${left}px` }
})

function onSelect(booking, event) {
  emit('select', booking, event.currentTarget.getBoundingClientRect())
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.12s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.pop-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.pop-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.pop-enter-from, .pop-leave-to { opacity: 0; transform: scale(0.96); }
</style>
